import { forwardRef } from "react";
import { amountInWords, formatINR, gstSplit } from "@/lib/invoiceUtils";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const InvoicePreview = forwardRef(function InvoicePreview(
  { business, customer, shipping, eway, meta, items, taxRate, taxLabel, totals },
  ref,
) {
  const visibleItems = items.length ? items : [];
  const split = gstSplit({
    subtotal: totals.subtotal,
    taxRate,
    companyGstin: business?.gstin,
    customerGstin: customer?.gstin,
  });
  const showShipTo = !!shipping?.enabled;

  return (
    <div
      ref={ref}
      data-testid="invoice-document"
      className="invoice-document w-full max-w-[210mm] min-h-[297mm] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)] ring-1 ring-[#E5E5E5] p-10 lg:p-12 shrink-0 relative"
      style={{ color: "#0A0A0A" }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-[#0A0A0A]">
        <div className="flex items-start gap-4">
          {business.logoDataUrl ? (
            <img
              src={business.logoDataUrl}
              alt="logo"
              className="w-16 h-16 object-contain border border-[#E5E5E5] p-1 bg-white"
            />
          ) : null}
          <div>
            <h2
              data-testid="preview-business-name"
              className="text-2xl font-extrabold tracking-tighter leading-tight"
            >
              {business.name || "Your Business Name"}
            </h2>
            {business.tagline && (
              <p
                data-testid="preview-business-tagline"
                className="text-[11px] italic text-[#52525B] mt-0.5"
              >
                {business.tagline}
              </p>
            )}
            {business.address && (
              <p className="text-[11px] text-[#52525B] mt-1 max-w-xs whitespace-pre-line leading-snug">
                {business.address}
              </p>
            )}
            <div className="text-[11px] text-[#52525B] mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
              {business.phone && <span>T · {business.phone}</span>}
              {business.email && <span>E · {business.email}</span>}
            </div>
            {business.gstin && (
              <p className="text-[10px] font-mono mt-1 tracking-wider">
                GSTIN: {business.gstin}
              </p>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#52525B]">
            Tax Invoice
          </div>
          <div className="text-3xl font-black tracking-tighter mt-1">
            INVOICE
          </div>
          <div
            data-testid="preview-invoice-number"
            className="font-mono text-xs mt-2 tracking-wider"
          >
            {meta.number || "—"}
          </div>
        </div>
      </div>

      {/* Meta + Bill To + Ship To */}
      <div
        className={`mt-6 grid gap-6 ${showShipTo ? "grid-cols-3" : "grid-cols-2"}`}
      >
        <div data-testid="preview-billto-block">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">
            Bill To
          </div>
          <div
            data-testid="preview-customer-name"
            className="text-base font-bold"
          >
            {customer.name || "Customer Name"}
          </div>
          {customer.address && (
            <p className="text-[11px] text-[#52525B] mt-1 whitespace-pre-line leading-snug">
              {customer.address}
            </p>
          )}
          {customer.phone && (
            <p className="text-[11px] text-[#52525B] mt-0.5">
              T · {customer.phone}
            </p>
          )}
          {customer.gstin && (
            <p className="text-[10px] font-mono mt-1 tracking-wider">
              GSTIN: {customer.gstin}
            </p>
          )}
        </div>

        {showShipTo && (
          <div data-testid="preview-shipto-block">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">
              Ship To
            </div>
            <div className="text-base font-bold">
              {shipping.name || customer.name || "Consignee"}
            </div>
            {shipping.address && (
              <p className="text-[11px] text-[#52525B] mt-1 whitespace-pre-line leading-snug">
                {shipping.address}
              </p>
            )}
            {shipping.phone && (
              <p className="text-[11px] text-[#52525B] mt-0.5">
                T · {shipping.phone}
              </p>
            )}
            {shipping.gstin && (
              <p className="text-[10px] font-mono mt-1 tracking-wider">
                GSTIN: {shipping.gstin}
              </p>
            )}
          </div>
        )}

        <div
          className={`grid gap-x-4 gap-y-3 text-right ${meta.dueDate ? "grid-cols-2" : "grid-cols-1"}`}
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">
              Invoice Date
            </div>
            <div
              data-testid="preview-invoice-date"
              className="text-sm font-bold mt-1"
            >
              {formatDate(meta.date)}
            </div>
          </div>
          {meta.dueDate && (
            <div data-testid="preview-due-date-block">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">
                Due Date
              </div>
              <div className="text-sm font-bold mt-1">
                {formatDate(meta.dueDate)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* E-Way Bill strip */}
      {eway?.enabled && (
        <div
          data-testid="preview-eway-block"
          className="mt-5 border border-[#0A0A0A] grid grid-cols-2 md:grid-cols-5 text-[10px]"
        >
          {[
            ["E-Way Bill No.", eway.number],
            ["Date", formatDate(eway.date)],
            ["Vehicle", eway.vehicle],
            ["Transporter", eway.transporter],
            ["Place of Supply", eway.placeOfSupply],
          ].map(([label, value], i, arr) => (
            <div
              key={label}
              className={`px-3 py-2 ${i < arr.length - 1 ? "border-r border-[#E5E5E5]" : ""}`}
            >
              <div className="font-bold uppercase tracking-[0.18em] text-[#52525B]">
                {label}
              </div>
              <div className="font-mono mt-0.5 text-[11px] text-[#0A0A0A] truncate">
                {value || "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Line Items */}
      <div className="mt-8">
        <table className="invoice-table w-full">
          <thead>
            <tr>
              <th style={{ width: "6%" }}>#</th>
              <th style={{ width: "36%" }}>Item / Description</th>
              <th style={{ width: "12%" }}>HSN</th>
              <th style={{ width: "12%" }} className="text-right">
                Qty
              </th>
              <th style={{ width: "16%" }} className="text-right">
                Rate (₹)
              </th>
              <th style={{ width: "18%" }} className="text-right">
                Amount (₹)
              </th>
            </tr>
          </thead>
          <tbody data-testid="preview-line-items">
            {visibleItems.map((it, idx) => {
              const amount = Number(it.qty || 0) * Number(it.rate || 0);
              const unit = (it.unit || "").trim();
              return (
                <tr key={it.id} data-testid={`preview-line-${idx}`}>
                  <td className="font-mono">{String(idx + 1).padStart(2, "0")}</td>
                  <td>
                    <div className="font-bold">{it.name || "—"}</div>
                    {it.description && (
                      <div className="text-[10.5px] text-[#52525B] mt-0.5">
                        {it.description}
                      </div>
                    )}
                  </td>
                  <td className="font-mono">{it.hsn || "—"}</td>
                  <td className="text-right font-mono">
                    {it.qty || 0}
                    {unit && (
                      <span
                        className="ml-1 text-[10px] text-[#52525B]"
                        data-testid={`preview-line-unit-${idx}`}
                      >
                        {unit}
                      </span>
                    )}
                  </td>
                  <td className="text-right font-mono">
                    {formatINR(it.rate)}
                  </td>
                  <td className="text-right font-mono font-bold">
                    {formatINR(amount)}
                  </td>
                </tr>
              );
            })}
            {/* Filler rows to reach a stable footprint */}
            {Array.from({
              length: Math.max(0, 5 - visibleItems.length),
            }).map((_, i) => (
              <tr key={`filler-${i}`} aria-hidden="true">
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="mt-6 flex justify-end">
        <div className="w-full max-w-xs">
          <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
            <span className="text-[11px] uppercase tracking-widest font-bold text-[#52525B]">
              Subtotal
            </span>
            <span
              data-testid="preview-subtotal"
              className="text-sm font-bold font-mono"
            >
              ₹ {formatINR(totals.subtotal)}
            </span>
          </div>

          {split.mode === "intra" ? (
            <>
              <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
                <span className="text-[11px] uppercase tracking-widest font-bold text-[#52525B]">
                  CGST ({split.cgst.rate}%)
                </span>
                <span
                  data-testid="preview-cgst"
                  className="text-sm font-bold font-mono"
                >
                  ₹ {formatINR(split.cgst.amount)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
                <span className="text-[11px] uppercase tracking-widest font-bold text-[#52525B]">
                  SGST ({split.sgst.rate}%)
                </span>
                <span
                  data-testid="preview-sgst"
                  className="text-sm font-bold font-mono"
                >
                  ₹ {formatINR(split.sgst.amount)}
                </span>
              </div>
            </>
          ) : business?.gstin && customer?.gstin ? (
            <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
              <span className="text-[11px] uppercase tracking-widest font-bold text-[#52525B]">
                IGST ({split.igst.rate}%)
              </span>
              <span
                data-testid="preview-igst"
                className="text-sm font-bold font-mono"
              >
                ₹ {formatINR(split.igst.amount)}
              </span>
            </div>
          ) : (
            <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
              <span className="text-[11px] uppercase tracking-widest font-bold text-[#52525B]">
                {taxLabel || "Tax"} ({Number(taxRate || 0)}%)
              </span>
              <span
                data-testid="preview-tax"
                className="text-sm font-bold font-mono"
              >
                ₹ {formatINR(totals.tax)}
              </span>
            </div>
          )}

          <div className="invoice-amount-block flex justify-between py-3 mt-1">
            <span className="text-xs uppercase tracking-[0.2em] font-extrabold">
              Total Due
            </span>
            <span
              data-testid="preview-total"
              className="text-xl font-black font-mono tracking-tight"
            >
              ₹ {formatINR(totals.total)}
            </span>
          </div>

          {business?.gstin && customer?.gstin && (
            <div
              data-testid="preview-tax-mode"
              className="mt-2 text-[9px] font-bold uppercase tracking-[0.2em] text-[#52525B] text-right"
            >
              {split.mode === "intra"
                ? "Intra-state · CGST + SGST"
                : "Inter-state · IGST"}
            </div>
          )}
        </div>
      </div>

      {/* Amount in words */}
      <div className="mt-4 border border-[#E5E5E5] px-4 py-2.5">
        <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#52525B]">
          Amount in Words
        </div>
        <div
          data-testid="preview-amount-words"
          className="text-xs font-medium mt-0.5"
        >
          {amountInWords(totals.total)}
        </div>
      </div>

      {/* Payment Details strip */}
      {business?.paymentDetails && (
        <div
          data-testid="preview-payment-details"
          className="mt-4 border border-[#0A0A0A] p-3"
        >
          <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#0A0A0A] mb-1">
            Payment Details
          </div>
          <p className="text-[11px] leading-relaxed text-[#0A0A0A] whitespace-pre-line font-mono">
            {business.paymentDetails}
          </p>
        </div>
      )}

      {/* Notes / Terms (company-level, only when set) */}
      {(business?.notes || business?.terms) && (
        <div className="mt-5 grid grid-cols-2 gap-6 text-[11px] leading-relaxed text-[#52525B]">
          {business?.notes ? (
            <div data-testid="preview-notes-block">
              <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#0A0A0A] mb-1">
                Notes
              </div>
              <p className="whitespace-pre-line">{business.notes}</p>
            </div>
          ) : (
            <div />
          )}
          {business?.terms ? (
            <div data-testid="preview-terms-block">
              <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#0A0A0A] mb-1">
                Terms &amp; Conditions
              </div>
              <p className="whitespace-pre-line">{business.terms}</p>
            </div>
          ) : (
            <div />
          )}
        </div>
      )}

      {/* For [Company] / Authorised Signatory — inside the document body */}
      <div
        data-testid="preview-signatory-block"
        className="mt-10 flex justify-end"
      >
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B]">
            For
          </div>
          <div
            data-testid="preview-signatory-company"
            className="text-sm font-extrabold tracking-tight mt-0.5"
          >
            {business?.name || "—"}
          </div>
          <div className="mt-12 border-t border-[#0A0A0A] pt-1 inline-block min-w-[180px] text-[10px] uppercase tracking-[0.2em] font-bold">
            Authorised Signatory
          </div>
        </div>
      </div>
    </div>
  );
});

export default InvoicePreview;
